import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styles from './button.module.scss';
import Link from '../link/link';

/**
 * The button component will return a single HTML <button>, <a>,
 * or a <Link> component.
 *
 * @returns {JSX} A button or link.
 */
const Button = ({
  borderStyle = 'round',
  buttonContent,
  children,
  className,
  color = 'blue',
  disabled,
  onClick,
  onFocus,
  size,
  textStyle,
  to,
  target = '_self',
  ...props
}) => {
  // Concatenates the appropriate classes.
  const classes = classnames(styles.button, {
    [styles[borderStyle]]: borderStyle,
    [styles[color]]: !disabled,
    [styles[disabled]]: disabled,
    [styles.small]: size === 'small',
    [styles.large]: size === 'large',
    [styles[textStyle]]: textStyle,
    [className]: true
  });

  // If the `to` prop exists, return a link.
  if (to) {
    return (
      <Link
        aria-label={props['aria-label']}
        onClick={onClick}
        disabled={disabled || false}
        className={classes}
        to={to}
        target={target}
      >
        {children || buttonContent}
      </Link>
    );
  }

  // If the `to` prop does not exist, return an HTML <button>.
  return (
    <button
      aria-expanded={props['aria-expanded'] || null}
      onClick={onClick}
      onFocus={onFocus}
      disabled={disabled || false}
      className={classes}
      {...props}
    >
      {children || buttonContent}
    </button>
  );
};

Button.propTypes = {
  borderStyle: PropTypes.oneOf(['round', 'square']),
  buttonContent: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    'blueMedium',
    'white',
    'blueLight',
    'blue',
    'blueDark',
    false
  ]),
  disabled: PropTypes.oneOf(['disabled']),
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  size: PropTypes.oneOf(['large', 'small']),
  textStyle: PropTypes.oneOf(['upper']),
  to: PropTypes.string,
  target: PropTypes.string
};

export default Button;
